import { CACHE_TIME } from "@/zapi-frontend/shared/const/consts";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getClaims } from "../services/getClaims";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatAnalyzedDate } from "../lib/formatAnalyzedDate";
import { prepareClaims } from "../lib/prepareClaims";
import { TClaim, TClaims, TClaimTab, TViewMode } from "./types";
import { toast } from "sonner";
import { updateClaimReview } from "../services/updateClaimReview";
import {
  useClaimsStoreSelector,
  selectIsGenerating,
  selectIsInitialized,
  selectProgressMessage,
  useInitializeClaimsPolling
} from "@/zapi-frontend/features/AnalyzeClaims";

export function useClaims({ projectId }: { projectId: string }) {
  const { activeChapter } = useChapterStore();
  const chapterId = activeChapter?.id?.toString();

  const isGenerating = useClaimsStoreSelector(selectIsGenerating(chapterId));
  const isInitialized = useClaimsStoreSelector(selectIsInitialized(chapterId));
  const progressMessage = useClaimsStoreSelector(selectProgressMessage(chapterId));
  const queryClient = useQueryClient();
  const chapterContent = activeChapter?.activeMemo?.mdContent;

  const { data: claimsData, isLoading: isLoadingClaims } = useQuery({
    queryKey: ["claims", projectId, chapterId],
    queryFn: async () => {
      if (!chapterId || !projectId) return { claims: [], chapterUpdatedAt: new Date() };
      return await getClaims({ projectId, chapterId });
    },
    enabled: !!chapterId && !!projectId,
    refetchOnWindowFocus: false,
    retry: 1,
    staleTime: CACHE_TIME
  });

  const claims = useMemo(() => claimsData?.claims ?? [], [claimsData?.claims]);
  const chapterUpdatedAt = claimsData?.chapterUpdatedAt;

  const [frozenClaims, setFrozenClaims] = useState<TClaims | null>(null);
  const abortControllersRef = useRef<Map<number, AbortController>>(new Map());

  const reviewMutation = useMutation({
    mutationFn: (variables: Parameters<typeof updateClaimReview>[0]) => {
      const { claimId } = variables;

      if (abortControllersRef.current.has(claimId)) {
        abortControllersRef.current.get(claimId)?.abort();
        abortControllersRef.current.delete(claimId);
      }

      const abortController = new AbortController();
      abortControllersRef.current.set(claimId, abortController);

      return updateClaimReview({ ...variables, signal: abortController.signal });
    },
    onMutate: async (variables) => {
      const { isReviewed, claimId, reviewedReason } = variables;

      await queryClient.cancelQueries({ queryKey: ["claims", projectId, chapterId] });
      const previousClaimsData = queryClient.getQueryData<TClaims>(["claims", projectId, chapterId]);

      queryClient.setQueryData<TClaims>(["claims", projectId, chapterId], (old) => {
        if (!old) return old;

        const frozenData = {
          ...old,
          claims: old.claims.map((claim: TClaim) => {
            return claim.id === claimId
              ? {
                  ...claim,
                  isReviewedButFrozen: isReviewed,
                  reviewedReasonButFrozen: reviewedReason
                }
              : claim;
          })
        };

        const thisClaim = (frozenClaims || old).claims.find((c) => c.id === claimId);
        if (thisClaim) setFrozenClaims(thisClaim.isReviewed !== isReviewed ? frozenData : null);

        const realData = {
          ...old,
          claims: old.claims.map((claim: TClaim) =>
            claim.id === claimId
              ? {
                  ...claim,
                  isReviewed,
                  reviewedAt: isReviewed ? new Date() : null,
                  reviewedReason,
                  isReviewedButFrozen: undefined,
                  reviewedReasonButFrozen: undefined
                }
              : claim
          )
        };

        return realData;
      });

      return { previousClaimsData };
    },
    onError: (err, variables, context) => {
      console.log(err);
      if (err instanceof Error && err.name === "CanceledError") return;
      if (context?.previousClaimsData) {
        queryClient.setQueryData(["claims", projectId, chapterId], context.previousClaimsData);
      }
      toast.error("Failed to update claim review status");
    },
    onSettled: (data, error, variables) => {
      abortControllersRef.current.delete(variables.claimId);
    }
  });

  const [selectedTab, setSelectedTab] = useState<TClaimTab | null>(null);
  const [viewMode, setViewMode] = useState<TViewMode>("claim");
  const [selectedClaimIndex, setSelectedClaimIndex] = useState(0);

  function initializeSelectedTab(preparedClaims: ReturnType<typeof prepareClaims>) {
    if (!preparedClaims) return;
    const tab = Object.entries(preparedClaims).find(([, value]) => value.length > 0)?.[0];
    if (tab) setSelectedTab(tab as TClaimTab);
  }

  const filteredClaims = useMemo(() => {
    const claimsToUse = frozenClaims?.claims ?? claims;
    const preparedClaims = prepareClaims(claimsToUse, chapterContent);
    if (selectedTab === null) initializeSelectedTab(preparedClaims);
    return preparedClaims;
  }, [claims, frozenClaims, chapterContent, selectedTab]);

  useEffect(() => {
    setSelectedTab(null);
    setSelectedClaimIndex(0);
    setFrozenClaims(null);
  }, [chapterId]);

  useEffect(() => {
    if (!selectedTab) return;
    const currentTabLength = filteredClaims?.[selectedTab]?.length ?? 0;
    if (currentTabLength === 0) {
      const tabWithItems = (["red", "yellow", "green", "reviewed"] as TClaimTab[]).find(
        (tab) => (filteredClaims?.[tab]?.length ?? 0) > 0
      );
      if (tabWithItems) {
        setSelectedTab(tabWithItems);
        setSelectedClaimIndex(0);
      }
    }
  }, [selectedTab, filteredClaims]);

  const lastAnalyzedDate = useMemo(() => formatAnalyzedDate(claims), [claims]);

  const currentClaim = (() => {
    if (!selectedTab) return null;
    return filteredClaims?.[selectedTab]?.[selectedClaimIndex] ?? null;
  })();

  const handleReviewToggle = useCallback(
    (reviewedReason: "not_applicable" | "closed" | null) => {
      const claimId = currentClaim?.id;
      if (claimId) reviewMutation.mutate({ claimId, isReviewed: reviewedReason !== null, reviewedReason });
    },
    [currentClaim, selectedTab, filteredClaims, selectedClaimIndex]
  );

  const isNoClaimsYet = claims.length === 0;

  function changeTab(tab: TClaimTab) {
    setFrozenClaims(null);
    setSelectedTab(tab as TClaimTab);
    setSelectedClaimIndex(0);
  }

  const onActiveChapterAnalysisComplete = async () => {
    if (chapterId && projectId) {
      queryClient.invalidateQueries({ queryKey: ["claims", projectId, chapterId] });
      const { syncChapterContent, setActiveChapter } = useChapterStore.getState();
      await syncChapterContent(chapterId);
      setActiveChapter(chapterId);
    }
  };

  const changeClaimIndex = (direction: "prev" | "next", overrideFrozen?: boolean) => {
    const goNext = (p: number) => (frozenClaims === null && !overrideFrozen ? p + 1 : p);
    const goPrev = (p: number) => p - 1;
    const lambda = direction === "prev" ? goPrev : goNext;

    setFrozenClaims(null);
    setSelectedClaimIndex(lambda);
  };

  function navigateToAvailableCard() {
    if (!selectedTab) return;
    const currentTabClaims = filteredClaims?.[selectedTab] ?? [];
    const isLastClaim = selectedClaimIndex === currentTabClaims.length - 1;

    if (isLastClaim && selectedClaimIndex > 0) {
      changeClaimIndex("prev", true);
    } else {
      changeClaimIndex("next", true);
    }
  }

  useInitializeClaimsPolling({ projectId, chapterId, onComplete: onActiveChapterAnalysisComplete });

  const isAllowedToRegenerate = chapterUpdatedAt?.toISOString() !== activeChapter?.activeMemo?.contentUpdatedAt;

  const isChapterContentEmpty = !activeChapter?.activeMemo?.mdContent?.trim();

  return {
    currentClaim,
    isGenerating,
    progressMessage,
    filteredClaims,
    lastAnalyzedDate,
    isLoading: isLoadingClaims || !isInitialized,
    selectedTab,
    isNoClaimsYet,
    viewMode,
    handleReviewToggle,
    changeViewMode: setViewMode,
    changeTab,
    selectedClaimIndex,
    changeClaimIndex,
    onActiveChapterAnalysisComplete,
    isAllowedToRegenerate,
    chapterId,
    isChapterContentEmpty,
    navigateToAvailableCard
  };
}
